import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useTracker } from '@archipro-website/tracker';
import { useSharedTiles } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import { getSourceSection } from '~/utils/get-source-section';
import { trackSearchTileClick } from '~/modules/tracking/util/track-search-tile-click';
import { searchPageDataAtom } from '~/modules-rocco/search/states/search-page-data-atom';

/**
 * Hook that attaches tracking events to search page tiles.
 *
 * This hook should be called once where search results are displayed.
 */
export function useSearchTileClickTracking() {
    const tracker = useTracker();
    const pageData = useAtomValue(searchPageDataAtom);

    const onTileClick = useCallback(
        (
            targetRef: React.RefObject<HTMLElement | null>,
            itemID?: number,
            professionalID?: number,
        ) => {
            void targetRef;

            trackSearchTileClick({
                tracker,
                itemID,
                professionalID,
                query: pageData?.searchQuery?.trim() ?? '',
                page: pageData?.pagination.page ?? 1,
                filterStates: pageData?.filterStates,
            });
        },
        [
            pageData?.filterStates,
            pageData?.pagination.page,
            pageData?.searchQuery,
            tracker,
        ],
    );

    const onTileMediaSliderSlideChange = useCallback(
        (direction: 'CarouselPrev' | 'CarouselNext', itemID?: number) => {
            tracker.log(direction, {
                url: new URL(window.location.href),
                data: {
                    Source: getSourceSection(),
                    ExtraData: {
                        LibraryItemID: itemID,
                    },
                },
            });
        },
        [tracker],
    );

    /*
     * Memoised because `useSharedTiles` keys a layout effect on this object's
     * identity and publishes it to an atom every tile subscribes to. A fresh
     * literal here re-renders all 40 tiles in the grid synchronously, before
     * paint, on every render of the page.
     */
    const trackingConfig = useMemo(
        () => ({
            onClick: onTileClick,
            onTileMediaSliderSlideChange,
        }),
        [onTileClick, onTileMediaSliderSlideChange],
    );

    useSharedTiles({ trackingConfig });
}
